import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AuthContext } from '../../context/AuthContext';
import ChatMessage from './ChatMessage';
import { track } from '../../utils/analytics';
import './ChatInterface.css';
import { authenticatedFetch } from '../../utils/apiClient';

const ChatInterface = ({ 
  show, 
  onClose, 
  contextEssayId = null, 
  contextEssayAnswer = null,
  contextEssayFeedback = null,
  contextSubject = 'General',
  forceNewConversation = false,
  authToken, 
  API_BASE_URL,
  initialMessage = '',
  autoSend = false,
  onConversationCreated,
  writingAssistantMode = false,
  teacherFeedbackMode = false,
  languageTranslationMode = false,
  selectedLanguage = null,
  studyPlanningMode = false,
  autoExpand = false
}) => {
  const { userData } = useContext(AuthContext);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [chatLocked, setChatLocked] = useState(false);
  const [audioBlobs, setAudioBlobs] = useState(new Map()); // Store audio blobs by message ID
  const [isExpanded, setIsExpanded] = useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [showConversations, setShowConversations] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [quickActionSelected, setQuickActionSelected] = useState(false); // Track if a button was clicked
  
  // Add state for resizable chat window
  const [chatSize, setChatSize] = useState({
    width: 400,
    height: window.innerHeight - 100 // Dynamic height based on viewport
  });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState('');
  
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioChunksRef = useRef([]);
  const chatRef = useRef(null);
  const startResizeRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const lastAssistantMessageRef = useRef(null);
  const lastUserMessageRef = useRef(null);
  const autoSendProcessedRef = useRef(false);
  const autoSendTriggeredRef = useRef(false); // Prevent duplicate auto-send using ref for persistence
  const isSendingRef = useRef(false); // Track sending state immediately to prevent race conditions
  const skipLoadMessagesRef = useRef(false); // Skip loading messages during auto-send flow

  // Smart scroll behavior - scroll to top of new assistant messages, bottom for user messages
  const handleNewMessage = (newMessages, previousMessages) => {
    if (!newMessages || newMessages.length === 0) return;
    
    const lastMessage = newMessages[newMessages.length - 1];
    const previousLength = previousMessages ? previousMessages.length : 0;
    
    console.log('handleNewMessage called:', {
      newCount: newMessages.length,
      previousCount: previousLength,
      lastMessageType: lastMessage.sender_type,
      lastMessageThinking: lastMessage.is_thinking
    });
    
    // Check if we have a new completed assistant message (either new message or thinking->completed)
    const hasNewAssistantResponse = () => {
      // Case 1: New assistant message added
      if (newMessages.length > previousLength && lastMessage.sender_type === 'assistant' && !lastMessage.is_thinking) {
        return true;
      }
      
      // Case 2: Thinking message became a completed assistant message
      if (newMessages.length === previousLength && lastMessage.sender_type === 'assistant' && !lastMessage.is_thinking) {
        const previousLastMessage = previousMessages[previousLength - 1];
        if (previousLastMessage && previousLastMessage.is_thinking) {
          return true;
        }
      }
      
      return false;
    };
    
    // Check if we have a new user message
    const hasNewUserMessage = () => {
      if (newMessages.length <= previousLength) return false;
      
      // Check if any of the new messages are from a student
      const newMessagesAdded = newMessages.slice(previousLength);
      return newMessagesAdded.some(msg => msg.sender_type === 'student');
    };
    
    // Check if we have a new thinking message
    const hasNewThinkingMessage = () => {
      if (newMessages.length <= previousLength) return false;
      
      // Check if the last message is a new thinking message
      return lastMessage.sender_type === 'assistant' && lastMessage.is_thinking;
    };
    
    // If this is a new or updated assistant response, scroll to its top
    if (hasNewAssistantResponse()) {
      setTimeout(() => {
        if (lastAssistantMessageRef.current) {
          lastAssistantMessageRef.current.scrollIntoView({ 
            behavior: "smooth", 
            block: "start" 
          });
        }
      }, 100);
    } 
    // For new thinking messages, scroll to bottom to show AI is responding
    else if (hasNewThinkingMessage()) {
      console.log('Detected new thinking message, scrolling to bottom');
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
    // For new user messages (if no thinking message was added), scroll to show the message
    else if (hasNewUserMessage()) {
      console.log('Detected new user message, attempting to scroll');
      setTimeout(() => {
        if (lastUserMessageRef.current) {
          console.log('Scrolling to user message using ref');
          lastUserMessageRef.current.scrollIntoView({ 
            behavior: "smooth", 
            block: "end",
            inline: "nearest"
          });
        } else {
          console.log('User message ref not available, using fallback scroll');
          // Fallback to bottom scroll if ref not available
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    }
  };

  // Update mobile state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle scroll to detect if user is at the top
  const handleMessagesScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    setIsScrolledToTop(scrollTop <= 10); // Consider "top" if within 10px
  };

  // Track previous messages to detect new ones
  const previousMessages = useRef([]);
  
  useEffect(() => {
    handleNewMessage(messages, previousMessages.current);
    previousMessages.current = messages;
  }, [messages]);

  // Auto-expand when opened from StartHereFlow
  useEffect(() => {
    if (show && autoExpand) {
      setIsExpanded(true);
    }
  }, [show, autoExpand]);

  // Focus input when chat opens
  useEffect(() => {
    if (show && inputRef.current) {
      inputRef.current.focus();
    }
  }, [show]);

  // Set skip flag early if we're in auto-send mode to prevent race conditions
  useEffect(() => {
    if (show && autoSend && initialMessage && forceNewConversation) {
      skipLoadMessagesRef.current = true;
      console.log('Auto-send mode detected: setting skipLoadMessagesRef to true');
    }
  }, [show, autoSend, initialMessage, forceNewConversation]);

  // Load conversations when component mounts
  useEffect(() => {
    if (show) {
      loadConversations();
    } else {
      // Reset auto-send trigger when chat is closed
      autoSendTriggeredRef.current = false;
      skipLoadMessagesRef.current = false;
    }
  }, [show, userData]);

  // Load conversation messages when active conversation changes
  useEffect(() => {
    if (activeConversationId && !skipLoadMessagesRef.current) {
      loadMessages(activeConversationId);
    }
  }, [activeConversationId]);

  // Cleanup recording timer on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  // Handle mouse events for resizing
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || isExpanded) return;

      const deltaX = e.clientX - startResizeRef.current.x;
      const deltaY = e.clientY - startResizeRef.current.y;

      let newWidth = startResizeRef.current.width;
      let newHeight = startResizeRef.current.height;

      if (resizeDirection.includes('right')) {
        newWidth = Math.max(300, Math.min(800, startResizeRef.current.width + deltaX));
      }
      if (resizeDirection.includes('left')) {
        newWidth = Math.max(300, Math.min(800, startResizeRef.current.width - deltaX));
      }
      if (resizeDirection.includes('bottom')) {
        newHeight = Math.max(400, Math.min(window.innerHeight - 100, startResizeRef.current.height + deltaY));
      }
      if (resizeDirection.includes('top')) {
        newHeight = Math.max(400, Math.min(window.innerHeight - 100, startResizeRef.current.height - deltaY));
      }

      setChatSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection('');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = getCursor(resizeDirection);
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeDirection, isExpanded]);

  // Helper function to get cursor style based on resize direction
  const getCursor = (direction) => {
    if (direction === 'right' || direction === 'left') return 'ew-resize';
    if (direction === 'bottom' || direction === 'top') return 'ns-resize';
    if (direction === 'bottom-right' || direction === 'top-left') return 'nw-resize';
    if (direction === 'bottom-left' || direction === 'top-right') return 'ne-resize';
    return 'default';
  };

  // Handle resize start
  const handleResizeStart = (direction, e) => {
    if (isExpanded) return;
    
    e.preventDefault();
    setIsResizing(true);
    setResizeDirection(direction);
    startResizeRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: chatSize.width,
      height: chatSize.height
    };
  };

  const loadConversations = async () => {
    try {
      setIsLoading(true);
      console.log('loadConversations called with:', { 
        forceNewConversation, 
        contextEssayId,
        contextEssayAnswer: contextEssayAnswer?.substring(0, 50), 
        contextEssayFeedback: typeof contextEssayFeedback === 'string' ? contextEssayFeedback.substring(0, 50) : 'JSON object',
        contextSubject,
        autoSend,
        hasInitialMessage: !!initialMessage
      });
      
      const response = await authenticatedFetch(`${API_BASE_URL}/api/chat/conversations`);

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        
        console.log('Existing conversations:', data.conversations?.length || 0);
        console.log('forceNewConversation:', forceNewConversation);
        console.log('contextEssayId:', contextEssayId);
        console.log('has contextEssayAnswer:', !!contextEssayAnswer);
        console.log('has contextEssayFeedback:', !!contextEssayFeedback);
        
        // Handle auto-send flow from Start Here (only once)
        if (autoSend && initialMessage && forceNewConversation && !autoSendTriggeredRef.current) {
          console.log('Auto-send flow: creating conversation and sending initial message');
          autoSendTriggeredRef.current = true; // Mark as triggered to prevent duplicates
          skipLoadMessagesRef.current = true; // Skip loading messages during auto-send to prevent race condition
          
          const conversationData = { subject: contextSubject, title: 'Quick Start' };
          
          // Add writing assistant mode flag if enabled
          if (writingAssistantMode) {
            conversationData.activate_writing_assistant = true;
          }
          
          // Add teacher feedback mode flag if enabled
          if (teacherFeedbackMode) {
            conversationData.activate_teacher_feedback = true;
          }
          
          // Add language translation mode flag if enabled
          if (languageTranslationMode) {
            conversationData.activate_language_translation = true;
            conversationData.selected_language = selectedLanguage;
          }
          
          // Add study planning mode flag if enabled
          if (studyPlanningMode) {
            conversationData.activate_study_planning = true;
          }
          
          const created = await createConversation(conversationData);
          if (created) {
            // Ensure conversation is selected (createConversation should have done this, but be explicit)
            if (activeConversationId !== created.id) {
              console.log('Auto-send: ensuring conversation is selected', created.id);
              setActiveConversationId(created.id);
            }
            
            // Immediately show user message and thinking indicator in UI
            const userMsg = {
              id: 'temp-user-' + Date.now(),
              conversation_id: created.id,
              sender_type: 'student',
              content: initialMessage,
              timestamp: new Date().toISOString()
            };
            const thinkingMsg = {
              id: 'temp-thinking-' + Date.now(),
              conversation_id: created.id,
              sender_type: 'assistant',
              content: '',
              is_thinking: true,
              timestamp: new Date().toISOString()
            };
            console.log('Auto-send: setting temporary messages', [userMsg, thinkingMsg]);
            setMessages([userMsg, thinkingMsg]);
            
            // Send the message to backend
            setTimeout(async () => {
              console.log('Auto-send: sending initial message to conversation', created.id);
              try {
                const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${created.id}/messages`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ content: initialMessage })
                });
                
                if (response.ok) {
                  const data = await response.json();
                  console.log('Auto-send: message sent successfully, thinking message:', data.thinking_message);
                  // Replace temp messages with real ones from backend
                  setMessages([data.student_message, data.thinking_message]);
                  // Poll for response (keep skip flag true during polling to prevent interference)
                  pollForResponse(data.thinking_message.id, created.id);
                  // Re-enable message loading after a short delay to ensure polling has started
                  setTimeout(() => {
                    skipLoadMessagesRef.current = false;
                    console.log('Auto-send: re-enabled message loading after polling started');
                    // Manually trigger loadMessages since the useEffect might have been skipped
                    loadMessages(created.id);
                  }, 500);
                } else {
                  // Re-enable message loading even on error
                  skipLoadMessagesRef.current = false;
                  setError('Failed to send message');
                }
              } catch (err) {
                console.error('Auto-send error:', err);
                skipLoadMessagesRef.current = false; // Re-enable message loading on error
                setError('Failed to send message');
              }
            }, 50);
          } else {
            // Re-enable message loading if conversation creation failed
            skipLoadMessagesRef.current = false;
          }
          return; // Exit early - don't process other conditions
        }
        
        // Skip forceNewConversation processing if we've already handled auto-send
        // (auto-send uses forceNewConversation, so we don't want to process it again)
        if (autoSendTriggeredRef.current && forceNewConversation) {
          console.log('Skipping forceNewConversation processing - already handled by auto-send');
          return;
        }
        
        // If force new conversation is requested and we have context, create one
        if (forceNewConversation) {
          console.log('Processing forceNewConversation=true');
          if (contextEssayId) {
            console.log('Creating new conversation for essay (with essay ID)');
            // Prioritize essay ID context when available
            createConversationForEssay();
          } else if (contextEssayAnswer || contextEssayFeedback) {
            console.log('Creating new conversation for context (without essay ID)');
            // Create a new conversation for context without essay ID
            createConversationForContext();
          } else {
            console.log('forceNewConversation=true but no context available - user can start typing');
            // If forced new conversation but no context, just let user start typing
            // The conversation will be created automatically when they send their first message
          }
        } else if ((contextEssayId || contextEssayAnswer || contextEssayFeedback) && data.conversations.length === 0) {
          console.log('Creating new conversation for available context');
          // If there's any context available and no active conversation, create one
          if (contextEssayId) {
            createConversationForEssay();
          } else {
            createConversationForContext();
          }
        } else if (data.conversations.length > 0 && !activeConversationId) {
          console.log('Using existing conversation:', data.conversations[0].id);
          setActiveConversationId(data.conversations[0].id);
        }
        // Removed the else case that was forcing the dialog - now users can just start typing!
      } else {
        throw new Error('Failed to load conversations');
      }
    } catch (err) {
      console.error('Error in loadConversations:', err);
      setError('Failed to load conversations: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (conversationId) => {
    // Skip loading messages if we're in auto-send flow to prevent race condition
    if (skipLoadMessagesRef.current) {
      console.log('Skipping loadMessages due to auto-send flow');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      } else {
        throw new Error('Failed to load messages');
      }
    } catch (err) {
      setError('Failed to load messages: ' + err.message);
    }
  };

  const createConversation = async (conversationData) => {
    try {
      console.log('Creating conversation with data:', conversationData);
      
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(conversationData)
      });

      if (response.ok) {
        const data = await response.json();
        const newConversation = data.conversation;
        console.log('Successfully created conversation:', newConversation);
        
        setConversations(prev => [newConversation, ...prev]);
        setActiveConversationId(newConversation.id);
        if (typeof onConversationCreated === 'function') {
          try { onConversationCreated(newConversation.id); } catch (_) {}
        }
        
        // Track new conversation creation
        track('chat_conversation_created', {
          subject: conversationData.subject,
          has_context: !!conversationData.context_essay_id,
          title_type: conversationData.title === 'Essay Discussion' ? 'generic' : 'specific'
        });
        
        return newConversation;
      } else {
        const errorData = await response.json();
        console.error('Failed to create conversation:', response.status, errorData);
        throw new Error(`Failed to create conversation: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error in createConversation:', err);
      setError('Failed to create conversation: ' + err.message);
    }
  };

  const createConversationForEssay = () => {
    console.log('createConversationForEssay called with contextEssayId:', contextEssayId);
    
    // Generate a meaningful title based on the essay question/topic
    let title = 'Essay Discussion';
    
    // If we have context essay answer or feedback, try to extract the topic
    if (contextEssayAnswer) {
      console.log('Trying to extract title from contextEssayAnswer');
      // Try to find the question/topic from the answer context
      const questionMatch = contextEssayAnswer.match(/Question[:\s]+([^\n]+)/i);
      if (questionMatch && questionMatch[1]) {
        const question = questionMatch[1].trim();
        // Truncate if too long and create a meaningful title
        title = question.length > 50 ? question.substring(0, 50) + '...' : question;
        console.log('Extracted title from question match:', title);
      } else {
        // If no question found, use first few words of the answer
        const firstLine = contextEssayAnswer.split('\n')[0].trim();
        if (firstLine && firstLine.length > 10) {
          title = 'Essay: ' + (firstLine.length > 40 ? firstLine.substring(0, 40) + '...' : firstLine);
          console.log('Extracted title from first line:', title);
        }
      }
    } else if (contextEssayFeedback) {
      console.log('Trying to extract title from contextEssayFeedback');
      // Handle both string and JSON feedback
      let feedbackText = '';
      if (typeof contextEssayFeedback === 'string') {
        feedbackText = contextEssayFeedback;
      } else if (typeof contextEssayFeedback === 'object' && contextEssayFeedback._category) {
        // For JSON feedback, use the category to create a meaningful title
        title = `${contextEssayFeedback._category} Essay Discussion`;
        console.log('Extracted title from JSON feedback category:', title);
      }
      
      if (feedbackText) {
        // Try to extract topic from string feedback
        const topicMatch = feedbackText.match(/(?:topic|question|about)[:\s]*([^\n.!?]+)/i);
        if (topicMatch && topicMatch[1]) {
          const topic = topicMatch[1].trim();
          title = topic.length > 50 ? topic.substring(0, 50) + '...' : topic;
          console.log('Extracted title from feedback:', title);
        }
      }
    }
    
    // If still generic, add some context
    if (title === 'Essay Discussion' && contextSubject && contextSubject !== 'General') {
      title = `${contextSubject} Essay Discussion`;
      console.log('Using subject-based title:', title);
    }
    
    console.log('Creating conversation with title:', title);
    
    const conversationData = {
      title: title,
      subject: contextSubject || 'English',
      context_essay_id: contextEssayId
    };
    
    console.log('Calling createConversation with:', conversationData);
    createConversation(conversationData);
  };

  const createConversationForContext = () => {
    // Generate a meaningful title based on content
    let title = `${contextSubject} Discussion`;
    
    // For essay-like content, try to extract a topic
    if (contextEssayAnswer && contextSubject && (contextSubject.includes('English') || contextSubject.includes('Literature'))) {
      // Try to find the question/topic from the answer context
      const questionMatch = contextEssayAnswer.match(/Question[:\s]+([^\n]+)/i);
      if (questionMatch && questionMatch[1]) {
        const question = questionMatch[1].trim();
        title = question.length > 50 ? question.substring(0, 50) + '...' : question;
      } else {
        // If no question found, use first few words of the answer
        const firstLine = contextEssayAnswer.split('\n')[0].trim();
        if (firstLine && firstLine.length > 10) {
          title = 'Essay: ' + (firstLine.length > 40 ? firstLine.substring(0, 40) + '...' : firstLine);
        }
      }
    } else if (contextEssayFeedback) {
      // Handle both string and JSON feedback
      let feedbackText = '';
      if (typeof contextEssayFeedback === 'string') {
        feedbackText = contextEssayFeedback;
      } else if (typeof contextEssayFeedback === 'object' && contextEssayFeedback._category) {
        // For JSON feedback, use the category to create a title
        title = `${contextEssayFeedback._category} Essay Discussion`;
      }
      
      if (feedbackText) {
        // Try to extract topic from string feedback
        const topicMatch = feedbackText.match(/(?:topic|question|about)[:\s]*([^\n.!?]+)/i);
        if (topicMatch && topicMatch[1]) {
          const topic = topicMatch[1].trim();
          title = topic.length > 50 ? topic.substring(0, 50) + '...' : topic;
        }
      }
    } else if (contextSubject === 'Mathematics') {
      title = 'Math Problem Discussion';
    }
    
    const conversationData = {
      title: title,
      subject: contextSubject,
      context_essay_id: null, // No essay ID for this type of context
      context_content_answer: contextEssayAnswer,
      context_content_feedback: typeof contextEssayFeedback === 'string' ? contextEssayFeedback : JSON.stringify(contextEssayFeedback)
    };
    
    console.log('createConversationForContext called with data:', {
      ...conversationData,
      context_content_answer: conversationData.context_content_answer?.substring(0, 100),
      context_content_feedback: conversationData.context_content_feedback?.substring(0, 100)
    });
    
    createConversation(conversationData);
  };

  const createQuickConversation = async (firstMessage, activateStudyPlanning = false) => {
    try {
      // Get LLM analysis for smart subject and title detection
      let title = 'Quick Chat';
      let subject = contextSubject || 'General';
      
      try {
        console.log('Analyzing message with LLM for smart subject detection');
        const analysisResponse = await fetch(`${API_BASE_URL}/api/chat/analyze-message`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            message: firstMessage,
            current_subject: contextSubject || 'General'
          })
        });

        if (analysisResponse.ok) {
          const analysis = await analysisResponse.json();
          console.log('LLM analysis result:', analysis);
          
          // Use LLM suggestions
          subject = analysis.subject || subject;
          title = analysis.title || title;
          
          // Log confidence for debugging
          if (analysis.confidence) {
            console.log(`LLM confidence: ${analysis.confidence}`);
          }
        } else {
          console.warn('LLM analysis failed, using fallback');
        }
      } catch (analysisError) {
        console.warn('Error getting LLM analysis, using fallback:', analysisError);
        
        // Fallback to basic title generation if LLM fails
        if (firstMessage.length > 10) {
          const messageWords = firstMessage.trim().split(' ');
          if (messageWords.length >= 3) {
            const titleCandidate = messageWords.slice(0, 6).join(' ');
            title = titleCandidate.length > 40 ? titleCandidate.substring(0, 40) + '...' : titleCandidate;
          }
        }
      }
      
      const conversationData = {
        title: title,
        subject: subject,
        context_essay_id: contextEssayId,
        context_content_answer: contextEssayAnswer,
        context_content_feedback: typeof contextEssayFeedback === 'string' ? contextEssayFeedback : JSON.stringify(contextEssayFeedback),
        activate_study_planning: activateStudyPlanning
      };
      
      console.log('Creating quick conversation with LLM-analysed data:', conversationData);
      
      const conversation = await createConversation(conversationData);
      
      if (conversation) {
        // Now send the first message using the conversation ID directly
        console.log('Sending first message after conversation creation');
        await sendFirstMessage(firstMessage, conversation.id);
      }
    } catch (err) {
      console.error('Error creating quick conversation:', err);
      setError('Failed to start conversation: ' + err.message);
      setIsSending(false);
    }
  };

  const sendFirstMessage = async (messageText, conversationId = null) => {
    try {
      // Use passed conversationId or fall back to state
      const targetConversationId = conversationId || activeConversationId;
      
      if (!targetConversationId) {
        throw new Error('No conversation ID available');
      }
      
      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${targetConversationId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: messageText })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Add both student message and thinking indicator
        setMessages(prev => [...prev, data.student_message, data.thinking_message]);
        
        // Track text message sent
        track('chat_message_sent', {
          type: 'text',
          conversation_id: targetConversationId,
          message_length: messageText.length
        });
        
        // Poll for assistant response
        pollForResponse(data.thinking_message.id, targetConversationId);
      } else {
        const errorData = await response.json();
        if (errorData.error === 'feature_limit_reached') {
          // Teachers should have unlimited access, so this shouldn't happen for them
          if (userData?.role !== 'teacher') {
            const lockedMessage = {
              id: 'system-upgrade',
              conversation_id: targetConversationId,
              sender_type: 'assistant',
              content: `You're flying through your study! You've reached this week's free chat limit.\n\n- **Upgrade to Pulc Premium** for unlimited AI chat, essays, math help, quizzes, and more.\n\n[Subscribe to Premium](/subscribe)`,
              timestamp: new Date().toISOString(),
              is_thinking: false
            };
            setMessages(prev => [...prev, lockedMessage]);
            setChatLocked(true);
          }
          setError(null);
          return;
        }
        throw new Error(errorData.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  const sendMessage = async () => {
    // Check both state and ref to prevent race conditions
    if (!newMessage.trim() || isSending || isSendingRef.current) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setIsSending(true);
    isSendingRef.current = true; // Set ref immediately to prevent rapid double-clicks

    try {
      // If no active conversation, create one automatically
      if (!activeConversationId) {
        console.log('No active conversation, creating one automatically');
        await createQuickConversation(messageText);
        return; // createQuickConversation will handle sending the message
      }

      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${activeConversationId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: messageText })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Add both student message and thinking indicator
        setMessages(prev => [...prev, data.student_message, data.thinking_message]);
        
        // Track text message sent
        track('chat_message_sent', {
          type: 'text',
          conversation_id: activeConversationId,
          message_length: messageText.length
        });
        
        // Poll for assistant response
        pollForResponse(data.thinking_message.id, activeConversationId);
      } else {
        const errorData = await response.json();
        if (errorData.error === 'feature_limit_reached') {
          // Teachers should have unlimited access, so this shouldn't happen for them
          if (userData?.role !== 'teacher') {
            const lockedMessage = {
              id: 'system-upgrade',
              conversation_id: activeConversationId,
              sender_type: 'assistant',
              content: `You're flying through your study! You've reached this week's free chat limit.\n\n- **Upgrade to Pulc Premium** for unlimited AI chat, essays, math help, quizzes, and more.\n\n[Subscribe to Premium](/subscribe)`,
              timestamp: new Date().toISOString(),
              is_thinking: false
            };
            setMessages(prev => [...prev, lockedMessage]);
            setChatLocked(true);
          }
          setError(null);
          return;
        }
        throw new Error(errorData.error || 'Failed to send message');
      }
    } catch (err) {
      setError('Failed to send message: ' + err.message);
    } finally {
      setIsSending(false);
      isSendingRef.current = false; // Reset ref
    }
  };

  const sendAudioMessage = async (audioBlob) => {
    console.log('sendAudioMessage called with blob size:', audioBlob.size);
    if (isSending) {
      console.log('Cannot send - already sending:', isSending);
      return;
    }

    setIsSending(true);
    console.log('Starting audio message send...');

    // If no active conversation, create one automatically with audio
    let targetConversationId = activeConversationId;
    
    if (!targetConversationId) {
      console.log('No active conversation, creating one for audio message');
      try {
        // For audio messages, we'll create a simple conversation since we don't know the content yet
        // The LLM can analyze the content after transcription in the backend
        const conversationData = {
          title: 'Voice Chat',
          subject: contextSubject || 'General',
          context_essay_id: contextEssayId,
          context_content_answer: contextEssayAnswer,
          context_content_feedback: typeof contextEssayFeedback === 'string' ? contextEssayFeedback : JSON.stringify(contextEssayFeedback)
        };
        
        const conversation = await createConversation(conversationData);
        if (!conversation) {
          throw new Error('Failed to create conversation for audio message');
        }
        targetConversationId = conversation.id;
        // Continue with sending the audio message below
      } catch (err) {
        console.error('Error creating conversation for audio:', err);
        setError('Failed to start conversation for audio message: ' + err.message);
        setIsSending(false);
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      console.log('FormData created, sending to:', `${API_BASE_URL}/api/chat/conversations/${targetConversationId}/messages/audio`);

      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${targetConversationId}/messages/audio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
        body: formData
      });

      console.log('Response received:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('Audio message sent successfully:', data);
        
        // Store the audio blob for playback
        setAudioBlobs(prev => {
          const newMap = new Map(prev);
          newMap.set(data.student_message.id, audioBlob);
          return newMap;
        });
        
        // Add both student message and thinking indicator
        setMessages(prev => [...prev, data.student_message, data.thinking_message]);
        
        // Track audio message sent
        track('chat_message_sent', {
          type: 'audio',
          conversation_id: targetConversationId,
          audio_duration: recordingDuration
        });
        
        // Poll for assistant response
        pollForResponse(data.thinking_message.id, targetConversationId);
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        if (errorData.error === 'feature_limit_reached') {
          // Teachers should have unlimited access, so this shouldn't happen for them
          if (userData?.role !== 'teacher') {
            const lockedMessage = {
              id: 'system-upgrade',
              conversation_id: targetConversationId,
              sender_type: 'assistant',
              content: `You're flying through your study! You've reached this week's free chat limit.\n\n- **Upgrade to Pulc Premium** for unlimited AI chat, essays, math help, quizzes, and more.\n\n[Subscribe to Premium](/subscribe)`,
              timestamp: new Date().toISOString(),
              is_thinking: false
            };
            setMessages(prev => [...prev, lockedMessage]);
            setChatLocked(true);
          }
          setError(null);
          return;
        }
        throw new Error(errorData.error || 'Failed to send audio message');
      }
    } catch (err) {
      console.error('Error sending audio message:', err);
      setError('Failed to send audio message: ' + err.message);
    } finally {
      setIsSending(false);
    }
  };

  const startRecording = async () => {
    try {
      // Track voice recording started
      track('chat_voice_recording_started');
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try different audio formats for better compatibility
      let options = {};
      
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options = { mimeType: 'audio/webm;codecs=opus' };
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        options = { mimeType: 'audio/webm' };
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options = { mimeType: 'audio/mp4' };
      } else {
        options = {}; // Use browser default
      }
      
      const recorder = new MediaRecorder(stream, options);
      console.log('Using audio format:', recorder.mimeType);
      
      audioChunksRef.current = [];
      setRecordingDuration(0);
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('Audio data available, size:', event.data.size);
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      setIsRecording(true);
      recorder.start();

      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Microphone access error:', err);
      setError('Failed to access microphone: ' + err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }

      // Handle the audio when recording stops
      mediaRecorder.onstop = () => {
        console.log('Recording stopped, audioChunks:', audioChunksRef.current);
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        console.log('Created audio blob, size:', audioBlob.size, 'type:', mimeType);
        if (audioBlob.size > 0) {
          sendAudioMessage(audioBlob);
        } else {
          console.error('Audio blob is empty');
          setError('Recording failed - please try again');
        }
        setIsRecording(false);
      };

      mediaRecorder.stop();
    }
  };

  const cancelRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      audioChunksRef.current = [];
      setRecordingDuration(0);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const playAudioMessage = (messageId) => {
    const audioBlob = audioBlobs.get(messageId);
    if (audioBlob) {
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl); // Clean up the URL
      };
      
      audio.play().catch(err => {
        console.error('Error playing audio:', err);
      });
    }
  };

  const pollForResponse = (thinkingMessageId, conversationIdOverride = null) => {
    const pollInterval = setInterval(async () => {
      try {
        const convId = conversationIdOverride || activeConversationId;
        if (!convId) { throw new Error('No conversation ID available for polling'); }
        // Reload messages to check if thinking message was updated
        const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${convId}/messages`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch messages');
        }
        
        const data = await response.json();
        const updatedMessages = data.messages || [];
        
        // Update the messages state
        setMessages(updatedMessages);
        
        // Check if the thinking message still exists and is still thinking
        const thinkingMessage = updatedMessages.find(msg => msg.id === thinkingMessageId);
        
        if (!thinkingMessage || !thinkingMessage.is_thinking) {
          console.log('AI response received, stopping poll');
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.error('Error polling for response:', err);
        clearInterval(pollInterval);
      }
    }, 1000); // Poll every 1 second

    // Stop polling after 2 minutes
    setTimeout(() => {
      console.log('Polling timeout reached');
      clearInterval(pollInterval);
    }, 120000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Prevent double-submission via Enter key
      if (!isSending && newMessage.trim()) {
        sendMessage();
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (!show) return null;

  return (
    <div 
      ref={chatRef}
      className={`chat-dropdown ${isExpanded ? 'expanded' : ''}`}
      style={isExpanded ? {} : {
        width: `${chatSize.width}px`,
        height: `${chatSize.height}px`,
        maxHeight: `${chatSize.height}px`
      }}
    >
      <div className="chat-dropdown-header">
        <h3>Chat to Pulc!</h3>
        <div className="chat-header-buttons">
          <button 
            className="chat-expand-btn" 
            onClick={() => {
              const newExpandedState = !isExpanded;
              setIsExpanded(newExpandedState);
              
              // Track chat interface expansion
              track('chat_interface_toggled', {
                expanded: newExpandedState
              });
            }}
            title={isExpanded ? "Restore to dropdown" : "Expand to full page"}
          >
            {isExpanded ? '⤓' : '⤢'}
          </button>
          <button className="chat-close-btn" onClick={onClose}>×</button>
        </div>
      </div>

      <div className="chat-dropdown-body">
        {/* Chat Tabs - always show */}
        <div className="chat-tabs">
            <button 
              className={`chat-tab ${!showConversations ? 'active' : ''}`}
              onClick={() => setShowConversations(false)}
            >
              Current Chat
            </button>
            <button 
              className={`chat-tab ${showConversations ? 'active' : ''}`}
              onClick={() => setShowConversations(true)}
            >
              History{conversations.length > 0 ? ` (${conversations.length})` : ''}
            </button>
            <button 
              className="chat-tab new-chat"
              onClick={() => {
                // Create a new conversation with available context
                const newConversationData = {
                  title: 'New Chat',
                  subject: contextSubject || 'General',
                  context_essay_id: contextEssayId,
                  context_content_answer: contextEssayAnswer,
                  context_content_feedback: typeof contextEssayFeedback === 'string' 
                    ? contextEssayFeedback 
                    : JSON.stringify(contextEssayFeedback)
                };
                setQuickActionSelected(false); // Reset button selection for new conversation
                createConversation(newConversationData);
                setShowConversations(false);
              }}
              title="Start a new chat"
            >
              + New
            </button>
          </div>

        {/* Conversations List - only show when tab is active */}
        {showConversations && (
          <div className="conversations-list">
            {conversations.length === 0 ? (
              <div className="no-conversations">
                <p>No conversation history yet.</p>
                <p>Start a new chat to begin!</p>
              </div>
            ) : (
              conversations.map(conv => (
              <div
                key={conv.id}
                className={`conversation-item ${activeConversationId === conv.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveConversationId(conv.id);
                  setQuickActionSelected(false); // Reset button selection for new conversation
                  setShowConversations(false); // Switch back to chat view
                }}
              >
                <div className="conversation-title">
                  {conv.title || 'Untitled Chat'}
                </div>
                <div className="conversation-meta">
                  <span className="conversation-subject">{conv.subject}</span>
                  <span className="conversation-date">
                    {formatTimestamp(conv.last_message_at || conv.created_at)}
                  </span>
                </div>
              </div>
            )))}
          </div>
        )}

        {/* Active Chat Area - Only show when not viewing conversations */}
        {!showConversations && (
          <div className="active-chat">
            {activeConversationId && (
            <div 
              className="messages-area"
              ref={messagesAreaRef}
              onScroll={handleMessagesScroll}
            >
              {messages.map((message, index) => {
                // Simpler logic: check if this is the very last message of each type
                const isLastMessage = index === messages.length - 1;
                const isLastAssistantMessage = message.sender_type === 'assistant' && 
                  !message.is_thinking && 
                  // Check if this is the last assistant message by going backwards
                  !messages.slice(index + 1).some(m => m.sender_type === 'assistant' && !m.is_thinking);
                
                const isLastUserMessage = message.sender_type === 'student' && 
                  // Check if this is the last user message by going backwards
                  !messages.slice(index + 1).some(m => m.sender_type === 'student');
                
                // Debug logging
                if (isLastUserMessage) {
                  console.log('Assigning ref to last user message:', message.id, message.content?.substring(0, 50));
                }
                if (isLastAssistantMessage) {
                  console.log('Assigning ref to last assistant message:', message.id);
                }
                
                return (
                  <div 
                    key={message.id} 
                    className={`message ${message.sender_type}`}
                    ref={isLastAssistantMessage ? lastAssistantMessageRef : 
                         isLastUserMessage ? lastUserMessageRef : null}
                  >
                    <div className="message-content">
                      {message.content === '[Audio message sent]' && audioBlobs.has(message.id) ? (
                        <div className="audio-message">
                          <button 
                            className="audio-playback-btn"
                            onClick={() => playAudioMessage(message.id)}
                            title="Play audio message"
                          >
                            🎵 Play Audio Message
                          </button>
                        </div>
                      ) : (
                        <ChatMessage 
                          message={message} 
                          authToken={authToken} 
                          API_BASE_URL={API_BASE_URL} 
                          conversationId={activeConversationId}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}

          {(!activeConversationId || messages.length === 0) && (
            <div className="welcome-message">
              <h4>👋 Hi! I'm Pulc AI</h4>
              {contextEssayId ? (
                <p>I can see you're working on an essay. Ask me anything about your essay, the feedback you received, or how to improve it!</p>
              ) : contextEssayAnswer ? (
                <p>I can see you have some work to discuss. What would you like to know about it?</p>
              ) : (
                <>
                  <p>Ask me anything about your studies - I can help you with essays, maths, science questions, and more!</p>
                  
                  <div className="quick-actions">
                    <h5>What can I help with?</h5>
                    <div className="action-buttons">
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setQuickActionSelected(true);
                          setNewMessage("I need help with an essay");
                          setTimeout(() => sendMessage(), 100);
                        }}
                      >
                        <span className="action-icon">📝</span>
                        <span className="action-label">Essay Help</span>
                      </button>
                      <button 
                        className="action-btn"
                        onClick={async () => {
                          setQuickActionSelected(true);
                          // Create conversation with study planning mode activated
                          await createQuickConversation("Can you help me create a study plan and timetable?", true);
                        }}
                      >
                        <span className="action-icon">📊</span>
                        <span className="action-label">Study Planning</span>
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setQuickActionSelected(true);
                          setNewMessage("I need help solving a math problem");
                          setTimeout(() => sendMessage(), 100);
                        }}
                      >
                        <span className="action-icon">🧮</span>
                        <span className="action-label">Math Help</span>
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setQuickActionSelected(true);
                          setNewMessage("I have a science question");
                          setTimeout(() => sendMessage(), 100);
                        }}
                      >
                        <span className="action-icon">🔬</span>
                        <span className="action-label">Science</span>
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setQuickActionSelected(true);
                          setNewMessage("Just wanted to chat");
                          setTimeout(() => sendMessage(), 100);
                        }}
                      >
                        <span className="action-icon">💬</span>
                        <span className="action-label">General Chat</span>
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => {
                          setQuickActionSelected(true);
                          setNewMessage("I need help preparing for my exams");
                          setTimeout(() => sendMessage(), 100);
                        }}
                      >
                        <span className="action-icon">📚</span>
                        <span className="action-label">Exam Prep</span>
                      </button>
                      <button 
                        className="action-btn action-btn-other"
                        onClick={() => {
                          setQuickActionSelected(true);
                          // Focus the input after a brief delay
                          setTimeout(() => inputRef.current?.focus(), 100);
                        }}
                      >
                        <span className="action-icon">✏️</span>
                        <span className="action-label">Other</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Quick Reply Suggestions - show only after AI has responded (no thinking message) */}
          {messages.length > 0 && !isRecording && !isSending && activeConversationId && !messages.some(m => m.is_thinking) && (
            <div className="quick-replies">
              {[
                "Dumb it down for me",
                "Give me an example",
                "What should I do next?"
              ].map((suggestion, idx) => (
                <button
                  key={idx}
                  className="quick-reply-bubble"
                  onClick={async (e) => {
                    e.preventDefault();
                    // Check both state and ref to prevent race conditions
                    if (isSending || isSendingRef.current) return;
                    
                    const messageText = suggestion;
                    setNewMessage('');
                    setIsSending(true);
                    isSendingRef.current = true; // Set ref immediately

                    // Optimistic UI update - show messages immediately
                    const tempUserMsg = {
                      id: 'temp-user-' + Date.now(),
                      conversation_id: activeConversationId,
                      sender_type: 'student',
                      content: messageText,
                      timestamp: new Date().toISOString()
                    };
                    const tempThinkingMsg = {
                      id: 'temp-thinking-' + Date.now(),
                      conversation_id: activeConversationId,
                      sender_type: 'assistant',
                      content: '',
                      is_thinking: true,
                      timestamp: new Date().toISOString()
                    };
                    setMessages(prev => [...prev, tempUserMsg, tempThinkingMsg]);

                    try {
                      const response = await fetch(`${API_BASE_URL}/api/chat/conversations/${activeConversationId}/messages`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${authToken}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({ content: messageText })
                      });

                      if (response.ok) {
                        const data = await response.json();
                        // Replace temp messages with real ones
                        setMessages(prev => {
                          const filtered = prev.filter(m => !(typeof m.id === 'string' && m.id.startsWith('temp-')));
                          return [...filtered, data.student_message, data.thinking_message];
                        });
                        pollForResponse(data.thinking_message.id, activeConversationId);
                      } else {
                        const errorData = await response.json();
                        if (errorData.error === 'feature_limit_reached') {
                          setChatLocked(true);
                        }
                        // Remove temp messages on error
                        setMessages(prev => prev.filter(m => !(typeof m.id === 'string' && m.id.startsWith('temp-'))));
                        throw new Error(errorData.error || 'Failed to send message');
                      }
                    } catch (err) {
                      setError('Failed to send message: ' + err.message);
                      // Remove temp messages on error
                      setMessages(prev => prev.filter(m => !m.id.startsWith('temp-')));
                    } finally {
                      setIsSending(false);
                      isSendingRef.current = false; // Reset ref
                    }
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          <div className="chat-input-area">
            {isRecording ? (
              <div className="recording-ui">
                <div className="recording-info">
                  <div className="recording-indicator">
                    <span className="recording-dot"></span>
                    Recording... {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
                  </div>
                </div>
                <div className="recording-controls">
                  <button 
                    onClick={cancelRecording}
                    className="cancel-recording-btn"
                    title="Cancel recording"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={stopRecording}
                    className="stop-recording-btn"
                    title="Send recording"
                  >
                    Send
                  </button>
                </div>
              </div>
            ) : (
              <>
                <textarea
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    messages.length > 0
                      ? "Continue the conversation..." 
                      : (quickActionSelected || contextEssayId || contextEssayAnswer)
                      ? "Ask me anything! Try: 'Help me improve my essay' or 'Solve: 2x + 5 = 13'"
                      : "👆 Please select a topic above to start chatting"
                  }
                  className="chat-input"
                  rows="3"
                  disabled={(messages.length === 0 && !quickActionSelected && !contextEssayId && !contextEssayAnswer) || isSending || chatLocked}
                />
                <div className="input-controls">
                  <button 
                    onClick={startRecording}
                    disabled={isSending || chatLocked}
                    className="mic-btn"
                    title="Record voice message"
                  >
                    🎤
                  </button>
                  <button 
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || isSending || chatLocked}
                    className="send-btn"
                    title={activeConversationId ? "Send message" : "Start conversation with auto-detected topic"}
                  >
                    {isSending ? '...' : (activeConversationId ? 'Send' : 'Start')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        )}
      </div>



      {/* Error display */}
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {chatLocked && userData?.role !== 'teacher' && (
        <div className="chat-locked-banner">
          You've reached your free weekly chat limit. Upgrade to continue the conversation.
          <a href="/subscribe" target="_blank" rel="noreferrer">Subscribe to Premium</a>
        </div>
      )}

      {/* Resize handles - only show when not expanded */}
      {!isExpanded && (
        <>
          <div 
            className="resize-handle resize-right"
            onMouseDown={(e) => handleResizeStart('right', e)}
          />
          <div 
            className="resize-handle resize-bottom"
            onMouseDown={(e) => handleResizeStart('bottom', e)}
          />
          <div 
            className="resize-handle resize-bottom-right"
            onMouseDown={(e) => handleResizeStart('bottom-right', e)}
          />
        </>
      )}
    </div>
  );
};

export default ChatInterface; 